import React from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';

import { c_ScoreColumns } from './ScoreRowsTable';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// The most runs the graph draws: the newest ones, so the date labels along the bottom never run into each other.
// The site PDF report's graph draws as many (AEOSiteReportCreator c_MaxChartRuns).
const c_MaxGraphRuns = 10;

// One line colour per score, in the order of c_ScoreColumns: the colour of the score's category tag in the reports
// (answer readiness, semantic schema, entity clarity, content depth, technical structure).
const c_ScoreLineColors = {
	answer_readiness_score: '#528A9A',
	semantic_clarity_score: '#789D87',
	entity_clarity_score: '#CE843D',
	content_depth_score: '#61463A',
	technical_seo_score: '#857C5D'
};

// The marker drawn at each point of a score's line, by Chart.js point style, with its rotation in degrees.
const c_ScoreMarkers = {
	answer_readiness_score: { style: 'circle', rotation: 0 },
	semantic_clarity_score: { style: 'rect', rotation: 0 },
	entity_clarity_score: { style: 'triangle', rotation: 0 },
	content_depth_score: { style: 'rectRot', rotation: 0 },
	technical_seo_score: { style: 'triangle', rotation: 180 }
};

// The legend's marker shapes, drawn as SVG in a 16 by 16 box so each matches its line's point style: a circle, or
// the polygon's points.
const c_LegendShapes = {
	answer_readiness_score: { circle: true },
	semantic_clarity_score: { points: '2,2 14,2 14,14 2,14' },
	entity_clarity_score: { points: '8,1.5 15,14.5 1,14.5' },
	content_depth_score: { points: '8,0 16,8 8,16 0,8' },
	technical_seo_score: { points: '1,1.5 15,1.5 8,14.5' }
};

const c_LineWidth = 2;
const c_PointRadius = 4;

// The five site scores across runs: one line per score, one point per run on a shared date axis, fixed 0 to 100.
// runs is the dashboard's run list, newest first by the date its page data is from; its newest c_MaxGraphRuns are
// drawn, oldest first so time runs left to right.
function ScoreHistoryGraph({runs}){

	const format_run_date = (run_date)=>{
		const date = new Date(run_date);
		return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
	};

	const get_chart_data = ()=>{
		const ordered_runs = runs.slice(0, c_MaxGraphRuns).reverse();

		const datasets = c_ScoreColumns.map((column)=>{
			return {
				label: column.label,
				data: ordered_runs.map((run)=>{ return run.scoring ? run.scoring[column.key] : null; }),
				borderColor: c_ScoreLineColors[column.key],
				backgroundColor: c_ScoreLineColors[column.key],
				borderWidth: c_LineWidth,
				pointRadius: c_PointRadius,
				pointStyle: c_ScoreMarkers[column.key].style,
				pointRotation: c_ScoreMarkers[column.key].rotation,
				tension: 0
			};
		});

		return {
			labels: ordered_runs.map((run)=>{ return format_run_date(run.page_data_date); }),
			datasets
		};
	};

	const get_chart_options = ()=>{
		return {
			responsive: true,
			maintainAspectRatio: false,
			scales: {
				x: {
					ticks: {
						maxRotation: 0,
						autoSkip: false,
						font: {
							size: 11
						}
					}
				},
				y: {
					min: 0,
					max: 100,
					ticks: {
						callback: (value)=>{ return value + '%'; },
						font: {
							size: 11
						}
					}
				}
			},
			plugins: {
				// The legend is drawn as HTML under the chart, see render_legend: the canvas legend sets its text on a
				// middle baseline that sits above the centre of its markers.
				legend: {
					display: false
				},
				tooltip: {
					callbacks: {
						label: (context)=>{ return context.dataset.label + ': ' + Math.round(context.parsed.y) + '%'; }
					}
				}
			}
		};
	};

	// One score's legend marker, the shape of its line's points in its line's colour.
	const render_legend_marker = (column)=>{
		const shape = c_LegendShapes[column.key];
		const color = c_ScoreLineColors[column.key];

		return (
			<svg className="score-legend-marker" viewBox="0 0 16 16" aria-hidden="true">
				{shape.circle ? <circle cx="8" cy="8" r="7" fill={color}/> : <polygon points={shape.points} fill={color}/>}
			</svg>
		);
	};

	// The legend: each score's marker beside its name, the two centred on each other.
	const render_legend = ()=>{
		return (
			<div className="score-legend">
				{c_ScoreColumns.map((column)=>{
					return (
						<div key={column.key} className="score-legend-item">
							{render_legend_marker(column)}
							<span>{column.label}</span>
						</div>
					);
				})}
			</div>
		);
	};

	if(!runs || (runs.length === 0)){
		return null;
	}

	return (
		<React.Fragment>
			<div className="score-history-graph">
				<Line data={get_chart_data()} options={get_chart_options()}/>
			</div>
			{render_legend()}
		</React.Fragment>
	);
}

export default ScoreHistoryGraph;
